import React, { useState, useEffect } from 'react';
import { 
  Card, Form, Button, 
  Spinner, Alert, InputGroup 
} from 'react-bootstrap';
import { FaMoneyBillWave, FaEdit, FaSave } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';

const StopFees = () => {
  const [stopFee, setStopFee] = useState({ fee: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchStopFee = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('adminToken');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/get-stop-fee`, {
          headers: { 'x-auth-token': token }
        });
        setStopFee({ fee: response.data.stopFee });
      } catch (err) {
        console.error('Error fetching stop fee:', err);
        setError(err.response?.data?.error || 'Failed to load stop fee');
      } finally {
        setLoading(false);
      }
    };
    fetchStopFee();
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
    setError(''); // Clear any previous errors when starting to edit
  };

  const handleChange = (e) => {
    setStopFee({ fee: parseFloat(e.target.value) || 0 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/update-stop-fee`,
        { fee: stopFee.fee },
        { headers: { 'x-auth-token': token } }
      );
      
      if (response.data.success) {
        toast.success('Stop fee updated successfully');
        setIsEditing(false);
      } else {
        throw new Error(response.data.error || 'Update failed');
      }
    } catch (err) {
      console.error('Error updating stop fee:', err);
      const errorMsg = err.response?.data?.error || 
                      err.message || 
                      'Failed to update stop fee';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="stop-fees-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Stop Fees Management</h2>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body>
          {isEditing ? (
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Stop Fee Amount ($)</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FaMoneyBillWave />
                  </InputGroup.Text>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={stopFee.fee}
                    onChange={handleChange}
                    required
                  />
                </InputGroup>
              </Form.Group>
              <div className="d-flex gap-2">
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Save Changes
                    </>
                  )}
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => setIsEditing(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          ) : (
            <>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="mb-1">Current Stop Fee</h5>
                  <p className="display-6 mb-0">${stopFee.fee}</p>
                </div>
                <Button 
                  variant="primary" 
                  onClick={handleEdit}
                >
                  <FaEdit className="me-2" />
                  Edit
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default StopFees;